import UserDetailComponent from "@/components/UserDetailComponent";
import { getFlagUrl } from "@/config/apiUrl";
import {
  formatNumberWithoutCurrency,
  getCountryISOCode,
  displayPhoneNumber,
} from "@/helper/HelperFunction";
import classes from "./PostCompareCard.module.css";

const PostCompareCard = ({ data }) => {
  if (!data || !data.oldData || !data.newData) {
    return <div>No comparison data available</div>;
  }

  const oldData = data.oldData;
  const newData = data.newData;

  // Helper function to check if values are different
  const isDifferent = (oldVal, newVal) => {
    if (typeof oldVal === "object" && oldVal !== null) {
      return JSON.stringify(oldVal) !== JSON.stringify(newVal);
    }
    return String(oldVal || "") !== String(newVal || "");
  };

  // Helper function to get comparison class for old value (removed/changed)
  const getOldComparisonClass = (oldVal, newVal, fieldName) => {
    if (fieldName === "phone") {
      const oldPhone = `${oldVal?.dialCode || ""} ${oldVal?.number || ""}`;
      const newPhone = `${newVal?.dialCode || ""} ${newVal?.number || ""}`;
      if (oldPhone !== newPhone) {
        return classes.removed;
      }
      return "";
    }
    if (fieldName === "plant") {
      const oldPlant = `${oldVal?.name || ""}|${oldVal?.link || ""}`;
      const newPlant = `${newVal?.name || ""}|${newVal?.link || ""}`;
      if (oldPlant !== newPlant) {
        return classes.removed;
      }
      return "";
    }
    if (fieldName === "product") {
      const oldProduct = oldVal?.name || "";
      const newProduct = newVal?.name || "";
      if (oldProduct !== newProduct) {
        return classes.removed;
      }
      return "";
    }
    if (isDifferent(oldVal, newVal)) {
      return classes.removed;
    }
    return "";
  };

  // Helper function to get comparison class for new value (added/changed)
  const getNewComparisonClass = (oldVal, newVal, fieldName) => {
    if (fieldName === "phone") {
      const oldPhone = `${oldVal?.dialCode || ""} ${oldVal?.number || ""}`;
      const newPhone = `${newVal?.dialCode || ""} ${newVal?.number || ""}`;
      if (oldPhone !== newPhone) {
        return classes.added;
      }
      return "";
    }
    if (fieldName === "plant") {
      const oldPlant = `${oldVal?.name || ""}|${oldVal?.link || ""}`;
      const newPlant = `${newVal?.name || ""}|${newVal?.link || ""}`;
      if (oldPlant !== newPlant) {
        return classes.added;
      }
      return "";
    }
    if (fieldName === "product") {
      const oldProduct = oldVal?.name || "";
      const newProduct = newVal?.name || "";
      if (oldProduct !== newProduct) {
        return classes.added;
      }
      return "";
    }
    if (isDifferent(oldVal, newVal)) {
      return classes.added;
    }
    return "";
  };

  // Helper function to render value
  const renderValue = (value, type) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (type === "phone") {
      return displayPhoneNumber(value) || "-";
    }
    if (type === "link" && value) {
      return (
        <a href={value} target="_blank" rel="noopener noreferrer">
          {value}
        </a>
      );
    }
    return String(value);
  };

  return (
    <div className={classes.compareCard}>
      <div className={classes.compareHeader}>
        <div className={classes.compareColumn}>
          <h3 className={classes.columnTitle}>Previous Post</h3>
        </div>
        <div className={classes.compareColumn}>
          <h3 className={classes.columnTitle}>Recent Post</h3>
        </div>
      </div>

      {/* Plant Information */}
      <div className={classes.compareSection}>
        <div className={classes.sectionTitle}>Plant Information</div>
        <div className={classes.compareRow}>
          <div
            className={`${classes.compareColumn} ${getOldComparisonClass(
              oldData.plant,
              newData.plant,
              "plant"
            )}`}
          >
            <UserDetailComponent
              data={{
                title: oldData.plant?.name || "-",
                description: oldData.plant?.link || "-",
                image: oldData.plant?.image || "default.png",
              }}
              isPro={false}
              descType="link"
            />
          </div>
          <div
            className={`${classes.compareColumn} ${getNewComparisonClass(
              oldData.plant,
              newData.plant,
              "plant"
            )}`}
          >
            <UserDetailComponent
              data={{
                title: newData.plant?.name || "-",
                description: newData.plant?.link || "-",
                image: newData.plant?.image || "default.png",
              }}
              isPro={false}
              descType="link"
            />
          </div>
        </div>
      </div>

      {/* Product and Capacity */}
      <div className={classes.compareSection}>
        <div className={classes.sectionTitle}>Product & Capacity</div>
        <div className={classes.compareRow}>
          <div className={classes.compareColumn}>
            <div className={classes.productInfo}>
              <span
                className={`${classes.productName} ${getOldComparisonClass(
                  oldData.product,
                  newData.product,
                  "product"
                )}`}
              >
                {oldData.product?.name || "-"}
              </span>
              <p className={classes.capacity}>
                <span
                  className={getOldComparisonClass(
                    oldData.capacity,
                    newData.capacity,
                    "capacity"
                  )}
                >
                  {formatNumberWithoutCurrency(oldData.capacity || 0)}
                </span>{" "}
                <span
                  className={getOldComparisonClass(
                    oldData.unit,
                    newData.unit,
                    "unit"
                  )}
                >
                  {oldData.unit || ""}
                </span>
              </p>
            </div>
          </div>
          <div className={classes.compareColumn}>
            <div className={classes.productInfo}>
              <span
                className={`${classes.productName} ${getNewComparisonClass(
                  oldData.product,
                  newData.product,
                  "product"
                )}`}
              >
                {newData.product?.name || "-"}
              </span>
              <p className={classes.capacity}>
                <span
                  className={getNewComparisonClass(
                    oldData.capacity,
                    newData.capacity,
                    "capacity"
                  )}
                >
                  {formatNumberWithoutCurrency(newData.capacity || 0)}
                </span>{" "}
                <span
                  className={getNewComparisonClass(
                    oldData.unit,
                    newData.unit,
                    "unit"
                  )}
                >
                  {newData.unit || ""}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Country */}
      <div className={classes.compareSection}>
        <div className={classes.sectionTitle}>Country</div>
        <div className={classes.compareRow}>
          <div
            className={`${classes.compareColumn} ${getOldComparisonClass(
              oldData.country,
              newData.country,
              "country"
            )}`}
          >
            <div className={classes.countryInfo}>
              <img
                src={getFlagUrl(getCountryISOCode(oldData.country))}
                alt={oldData.country}
              />
              <p>{oldData.country || "-"}</p>
            </div>
          </div>
          <div
            className={`${classes.compareColumn} ${getNewComparisonClass(
              oldData.country,
              newData.country,
              "country"
            )}`}
          >
            <div className={classes.countryInfo}>
              <img
                src={getFlagUrl(getCountryISOCode(newData.country))}
                alt={newData.country}
              />
              <p>{newData.country || "-"}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Trade Options */}
      <div className={classes.compareSection}>
        <div className={classes.sectionTitle}>Trade Options</div>
        <div className={classes.compareRow}>
          <div className={classes.compareColumn}>
            <div className={classes.tradeOptions}>
              <div
                className={`${classes.tradeOption} ${getOldComparisonClass(
                  oldData.tradeLocally,
                  newData.tradeLocally,
                  "tradeLocally"
                )}`}
              >
                <span>Trade Locally:</span>{" "}
                {renderValue(oldData.tradeLocally, "boolean")}
              </div>
              <div
                className={`${classes.tradeOption} ${getOldComparisonClass(
                  oldData.tradeInternationally,
                  newData.tradeInternationally,
                  "tradeInternationally"
                )}`}
              >
                <span>Trade Internationally:</span>{" "}
                {renderValue(oldData.tradeInternationally, "boolean")}
              </div>
              <div
                className={`${classes.tradeOption} ${getOldComparisonClass(
                  oldData.captiveUsed,
                  newData.captiveUsed,
                  "captiveUsed"
                )}`}
              >
                <span>Captive Used:</span>{" "}
                {renderValue(oldData.captiveUsed, "boolean")}
              </div>
            </div>
          </div>
          <div className={classes.compareColumn}>
            <div className={classes.tradeOptions}>
              <div
                className={`${classes.tradeOption} ${getNewComparisonClass(
                  oldData.tradeLocally,
                  newData.tradeLocally,
                  "tradeLocally"
                )}`}
              >
                <span>Trade Locally:</span>{" "}
                {renderValue(newData.tradeLocally, "boolean")}
              </div>
              <div
                className={`${classes.tradeOption} ${getNewComparisonClass(
                  oldData.tradeInternationally,
                  newData.tradeInternationally,
                  "tradeInternationally"
                )}`}
              >
                <span>Trade Internationally:</span>{" "}
                {renderValue(newData.tradeInternationally, "boolean")}
              </div>
              <div
                className={`${classes.tradeOption} ${getNewComparisonClass(
                  oldData.captiveUsed,
                  newData.captiveUsed,
                  "captiveUsed"
                )}`}
              >
                <span>Captive Used:</span>{" "}
                {renderValue(newData.captiveUsed, "boolean")}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Seller Details */}
      <div className={classes.compareSection}>
        <div className={classes.sectionTitle}>Seller Details</div>
        <div className={classes.compareRow}>
          <div className={classes.compareColumn}>
            <div className={classes.sellerDetails}>
              <div
                className={`${classes.detailItem} ${getOldComparisonClass(
                  oldData.fullName,
                  newData.fullName,
                  "fullName"
                )}`}
              >
                <span className={classes.detailLabel}>Full Name:</span>
                <span className={classes.detailValue}>
                  {renderValue(oldData.fullName)}
                </span>
              </div>
              <div
                className={`${classes.detailItem} ${getOldComparisonClass(
                  oldData.email,
                  newData.email,
                  "email"
                )}`}
              >
                <span className={classes.detailLabel}>Email:</span>
                <span className={classes.detailValue}>
                  {renderValue(oldData.email)}
                </span>
              </div>
              <div
                className={`${classes.detailItem} ${getOldComparisonClass(
                  oldData.phone,
                  newData.phone,
                  "phone"
                )}`}
              >
                <span className={classes.detailLabel}>Phone:</span>
                <span className={classes.detailValue}>
                  {renderValue(oldData.phone, "phone")}
                </span>
              </div>
              <div
                className={`${classes.detailItem} ${getOldComparisonClass(
                  oldData.link,
                  newData.link,
                  "link"
                )}`}
              >
                <span className={classes.detailLabel}>Link:</span>
                <span className={classes.detailValue}>
                  {renderValue(oldData.link, "link")}
                </span>
              </div>
            </div>
          </div>
          <div className={classes.compareColumn}>
            <div className={classes.sellerDetails}>
              <div
                className={`${classes.detailItem} ${getNewComparisonClass(
                  oldData.fullName,
                  newData.fullName,
                  "fullName"
                )}`}
              >
                <span className={classes.detailLabel}>Full Name:</span>
                <span className={classes.detailValue}>
                  {renderValue(newData.fullName)}
                </span>
              </div>
              <div
                className={`${classes.detailItem} ${getNewComparisonClass(
                  oldData.email,
                  newData.email,
                  "email"
                )}`}
              >
                <span className={classes.detailLabel}>Email:</span>
                <span className={classes.detailValue}>
                  {renderValue(newData.email)}
                </span>
              </div>
              <div
                className={`${classes.detailItem} ${getNewComparisonClass(
                  oldData.phone,
                  newData.phone,
                  "phone"
                )}`}
              >
                <span className={classes.detailLabel}>Phone:</span>
                <span className={classes.detailValue}>
                  {renderValue(newData.phone, "phone")}
                </span>
              </div>
              <div
                className={`${classes.detailItem} ${getNewComparisonClass(
                  oldData.link,
                  newData.link,
                  "link"
                )}`}
              >
                <span className={classes.detailLabel}>Link:</span>
                <span className={classes.detailValue}>
                  {renderValue(newData.link, "link")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCompareCard;
